import React, { memo, useMemo } from 'react';
import AudioPlayer from 'react-h5-audio-player';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { checkIsDarkTheme } from 'utils/theme';

import 'react-h5-audio-player/lib/styles.css';

const customControls = [];

const getStyles = (theme, { isPlayingDisabled } = {}) => {
  const { palette } = theme;
  const isDarkTheme = checkIsDarkTheme(palette.mode);

  return {
    rhap_container: {
      flex: 1,
      maxWidth: '500px',
      background: 'transparent !important',
      boxShadow: 'none !important',
      borderRadius: '6px',
      // The player's own root (.rhap_container from the vendor stylesheet) carries a hardcoded
      // white background that leaks on dark; force it transparent so it inherits the page surface.
      '& .rhap_container': {
        background: 'transparent !important',
        boxShadow: 'none !important',
      },
      '& svg, .rhap_current-time, .rhap_total-time': {
        color: isDarkTheme ? palette.common.white : null,
      },
      '& .rhap_progress-bar': {
        backgroundColor: isDarkTheme ? palette.border.default : null,
      },
      '& .rhap_download-progress': {
        backgroundColor: isDarkTheme ? palette.border.light : null,
      },
      '& .rhap_controls-section': {
        flex: 'none',
      },
      '& .rhap_additional-controls, .rhap_volume-controls': {
        display: 'none',
      },
      '& .rhap_main-controls': {
        minWidth: '52px',
        minHeight: '40px',
      },
      '& .rhap_play-pause-button': {
        display: isPlayingDisabled ? 'none' : 'block',
      },
    },
  };
};

const AudioPlayerComponent = ({
  link = '',
  onPlay,
  onPlayError,
  onPause,
  isAutoPlayAfterSrcChanged = false,
  isPlayingDisabled = false,
}) => {
  const theme = useTheme();
  const classes = getStyles(theme, { isPlayingDisabled });

  const containerSx = useMemo(() => classes.rhap_container, [classes]);

  return (
    <Box sx={containerSx}>
      <AudioPlayer
        autoPlayAfterSrcChange={isAutoPlayAfterSrcChanged}
        customAdditionalControls={customControls}
        customVolumeControls={customControls}
        defaultCurrentTime="00:00"
        defaultDuration="00:00"
        layout="horizontal-reverse"
        showDownloadProgress={false}
        showJumpControls={false}
        src={link}
        onPause={onPause}
        onPlay={onPlay}
        onPlayError={onPlayError}
      />
    </Box>
  );
};

AudioPlayerComponent.propTypes = {
  link: PropTypes.string,
  isAutoPlayAfterSrcChanged: PropTypes.bool,
  isPlayingDisabled: PropTypes.bool,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onPlayError: PropTypes.func,
};

export default memo(AudioPlayerComponent);
